import { ArrowLeft } from 'lucide-react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { useSocialMediaContext } from 'features/social-media/SocialMediaContext';
import { exportContent } from 'features/social-media/utils/exportutils';
import { useState, useEffect } from 'react';
import SocialMediaDialog from './Dialog';
import toast from 'react-hot-toast';
import SavePublishDialog from './SavePublishDialog';
import { ISocialMediaContext } from '../../types';
import SaveDialog from './SaveDialog';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import AuthRequiredModal from './AuthRequiredModal';
import SaveRequiredModal from './SaveRequiredModal';

export default function ResponseHeader() {
  const [searchParams] = useSearchParams();
  const [localContent, setLocalContent] = useState<ISocialMediaContext[] | null>(null);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [postTitle, setPostTitle] = useState('Untitled Post');
  
  const {
    setIsEditing,
    isEditing,
    socialMediaContent,
    isPlatformConnected,
    getPlatformUsername,
    updateSocialMediaContent
  } = useSocialMediaContext();

  const [isExporting, setIsExporting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalType, setAuthModalType] = useState<'save' | 'export'>('save');
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const [saveRequiredOpen, setSaveRequiredOpen] = useState(false);
  const postId = socialMediaContent?.[0]?.postId;
  const isSaved = Boolean(postId);

  // Try to get content from localStorage on mount
  useEffect(() => {
    if (!socialMediaContent?.length) {  // Check if array is empty
      const savedContent = localStorage.getItem('socialMediaContent');
      if (savedContent) {
        try {
          const parsedContent = JSON.parse(savedContent) as ISocialMediaContext[];
          setLocalContent(parsedContent);
          updateSocialMediaContent(parsedContent);
          localStorage.removeItem('socialMediaContent'); // Clear after restoring
        } catch (error) {
          console.error('Error parsing saved content:', error);
        }
      }
    }
  }, [socialMediaContent, updateSocialMediaContent]);

  // Use either context content or local content
  const displayContent = socialMediaContent || localContent;

  // Get current platform from URL
  const currentPlatform = searchParams.get('name')?.toLowerCase() as 'linkedin' | 'x';
  const platformDisplayName = currentPlatform === 'x' ? 'X' : 'LinkedIn';

  // Get connection status and username
  const isConnected = currentPlatform ? isPlatformConnected(currentPlatform) : false;
  const username = currentPlatform ? getPlatformUsername(currentPlatform) : undefined;

  const checkAndProceed = (action: () => void, type: 'save' | 'export') => {
    if (!isAuthenticated) {
      setAuthModalType(type);
      setShowAuthModal(true);
      return;
    }
    action();
  };

  const handleExport = async () => {
    checkAndProceed(async () => {
      if (!displayContent?.length) {
        toast.error('No content to export');
        return;
      }

      setIsExporting(true);
      try {
        await exportContent(displayContent);
        toast.success('Content exported successfully');
      } catch (error) {
        console.error('Export failed:', error);
        toast.error('Failed to export content');
      } finally {
        setIsExporting(false);
      }
    }, 'export');
  };

  const handleSave = () => {
    checkAndProceed(() => {
      setIsSaveDialogOpen(true);
    }, 'save');
  };

  const handleSaveAndPublish = () => {
    // First check if user is authenticated
    if (!isAuthenticated) {
      setAuthModalType('save');
      setShowAuthModal(true);
      return;
    }
    
    // Then check if content has a postId (has been saved)
    const hasPostId = socialMediaContent?.[0]?.postId;
    
    if (!hasPostId) {
      setSaveRequiredOpen(true);
    } else {
      setPublishDialogOpen(true);
    }
  };

  return (
    <header className="flex items-center justify-between px-4 h-14 border-b">
      {/* Left section with back button and title */}
      <div className="flex items-center gap-2">
        {!isEditing ? (
          <Button
            variant="ghost"
            size="icon"
            className="hover:bg-gray-100 rounded-full"
            asChild
          >
            <NavLink to={'/social-media/post?name=' + currentPlatform}>
              <ArrowLeft className="h-4 w-4" />
            </NavLink>
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="hover:bg-gray-100 rounded-full"
            onClick={() => {
              if (isEditing) setIsEditing(false);
            }}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}

        <div className="flex items-center gap-2">
          <span>Social /</span>
          <span>
            {platformDisplayName}
            {' /'}
          </span>
          <span className="font-medium">{postTitle}</span>
          {/* Show username only on desktop */}
          {isConnected && username && (
            <span className="hidden lg:inline-block text-sm text-gray-500">
              ({username})
            </span>
          )}
        </div>
      </div>

      {/* Right section with action buttons - Only show on desktop */}
      <div className="hidden lg:flex items-center gap-3">
        {!isEditing && (
          <>
            <Button
              variant={'customSecondary'}
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>

            <div className="relative">
              {/* Coming Soon Badge */}
              <div className="absolute -top-2 -right-2 bg-blue-600 text-white px-1 py-1 rounded-full text-[6px] z-10 font-medium whitespace-nowrap">
                Coming Soon
              </div>
              <Button
                variant={'customSecondary'}
                size="sm"
                disabled={true}
              >
                Export
              </Button>
            </div>

            {!isSaved && 
              <Button
                variant={'customSecondary'}
                size="sm"
                onClick={handleSave}
              >
                Save
              </Button>
            }

            <Button
              variant={'customPrimary'}
              size="sm"
              onClick={handleSaveAndPublish}
            >
              Publish
            </Button>
            
          </>
        )}
      </div>

      {/* Connection Dialog */}
      <SocialMediaDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        platform={currentPlatform || 'x'}
        socialMediaContent={displayContent}
      />

      <SavePublishDialog 
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
      />

      {/* Save Dialog */}
      <SaveDialog
        isSaveDialogOpen={isSaveDialogOpen}
        setIsSaveDialogOpen={setIsSaveDialogOpen}
        postTitle={postTitle}
        setPostTitle={setPostTitle}
        platformName={currentPlatform}
      />

      {/* Add AuthRequiredModal */}
      <AuthRequiredModal
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
        type={authModalType}
      />

      {/* Add SaveRequiredModal */}
      <SaveRequiredModal
        isOpen={saveRequiredOpen}
        onOpenChange={setSaveRequiredOpen}
        postTitle={postTitle}
        setPostTitle={setPostTitle}
        platformName={currentPlatform}
      />
    </header>
  );
}